import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { all_routes } from '../../router/all_routes';
import { getAllLeaves, createLeaveRequest, syncEmployeeLeaves } from '../../../core/data/redux/leaveSlice';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

const LEAVE_LEGENDS = [
    { label: 'Casual Leave', color: '#fbc02d', bg: '#fff9c4' },
    { label: 'Child Care Leave', color: '#2196f3', bg: '#e3f2fd' },
    { label: 'Earned Leave-Regular', color: '#00bcd4', bg: '#e0f7fa' },
    { label: 'Joining Time-EL', color: '#ff9800', bg: '#fff3e0' },
    { label: 'Leave Half Ave.Pay FP', color: '#ffb74d', bg: '#fff8e1' },
    { label: 'Leave Half Ave.Pay(Half)', color: '#f57c00', bg: '#ffe0b2' },
    { label: 'Maternity Leave', color: '#e91e63', bg: '#fce4ec' },
    { label: 'Out Station Leave', color: '#9c27b0', bg: '#f3e5f5' },
    { label: 'Paternity Leave', color: '#3f51b5', bg: '#e8eaf6' },
    { label: 'Quarantine Leave', color: '#689f38', bg: '#f1f8e9' },
    { label: 'Restricted Holidays', color: '#795548', bg: '#efebe9' },
    { label: 'Special CL Blood Donation', color: '#f44336', bg: '#ffebee' },
    { label: 'Special CL Disability', color: '#9e9e9e', bg: '#f5f5f5' },
    { label: 'Special CL Sterlization', color: '#4caf50', bg: '#e8f5e9' },
    { label: 'Special CL(Other)', color: '#ffc107', bg: '#fff8e1' },
    { label: 'Study Leave', color: '#673ab7', bg: '#ede7f6' },
    { label: 'WRIIL', color: '#009688', bg: '#e0f2f1' },
    { label: 'Miscarriage Leave', color: '#00838f', bg: '#e0f7fa' },
];

const getLeaveColorProps = (leaveType: string) => {
    const found = LEAVE_LEGENDS.find(leg => leaveType.toLowerCase().includes(leg.label.toLowerCase().split(' ')[0]));
    if (found) return { bg: found.bg, color: found.color };
    const exact = LEAVE_LEGENDS.find(leg => leaveType.toLowerCase() === leg.label.toLowerCase());
    if (exact) return { bg: exact.bg, color: exact.color };
    return { bg: '#f8f9fa', color: '#6c757d' };
};

const LeaveManagementList = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const employeesState = useSelector((state: any) => state.employeesState);
    const empId = employeesState?.data?.data?.empId || "";

    const leavesState = useSelector((state: any) => state.leavesState);
    const { allLeaves, loading } = leavesState || {};

    const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        if (!empId) {
            dispatch(getMyProfile({}) as any);
        }
    }, [dispatch, empId]);

    useEffect(() => {
        if (empId) {
            dispatch(getAllLeaves({ EmployeeID: empId }));
        }
    }, [dispatch, empId]);

    const handleSyncSap = async () => {
        setIsSyncing(true);
        try {
            await dispatch(syncEmployeeLeaves({ empId })).unwrap();
            dispatch(getAllLeaves({ EmployeeID: empId }));
            toast.success("Leave records synced successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to sync leave records.");
        } finally {
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        if (allLeaves?.data) {
            const listData = Array.isArray(allLeaves.data) ? allLeaves.data : allLeaves.data.leaveRequests || [];
            setLeaveRequests(listData);
        }
    }, [allLeaves]);

    const formatDate = (dateString: string | null) => {
        if (!dateString || dateString === '-') return "-";
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) return dateString;
        try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            }
            return dateString;
        } catch (e) {
            return dateString || "-";
        }
    };

    const handleDelete = async (request: any) => {
        const result = await Swal.fire({
            title: 'Delete Request?',
            text: "Are you sure you want to delete this leave request? This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ab1f24',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Delete'
        });

        if (result.isConfirmed) {
            try {
                const payload = {
                    EmployeeID: String(request.EmployeeID || empId).trim().padStart(8, '0'),
                    RequestID: request.RequestID || request.id,
                    LeaveKey: request.LeaveKey || '',
                    ActionCode: 3,
                    Notes: '',
                    ProcessCheckOnlyInd: false,
                    ChangeStateID: request.ChangeStateID || 1,
                    StartDate: '00-00-0000',
                    EndDate: '00-00-0000',
                };

                await dispatch(createLeaveRequest(payload)).unwrap();
                toast.success("Leave request deleted successfully!");
                dispatch(getAllLeaves({ EmployeeID: empId }));
            } catch (err: any) {
                console.error("Failed to delete the leave request", err);
                toast.error(err?.message || err || "Failed to delete the request. Please try again.");
            }
        }
    };

    const handleViewDetails = (id: string) => {
        navigate(`${routes.LeaveManagement.details.replace(':id', id)}`);
    };

    // Reimbursement-style status badge colors
    const getStatusColor = (status: string | undefined) => {
        const s = (status || "").toUpperCase();
        if (["A", "APPROVED", "POSTED", "S", "SETTLED"].some(k => s.includes(k))) return "#28a745"; // Green
        if (["R", "REJECTED", "CANCELLED", "DELETED", "X"].some(k => s.includes(k))) return "#dc3545"; // Red
        if (["SENT", "PENDING", "TO BE APPROVED", "NEW", "T", "P"].some(k => s.includes(k))) return "#f39c12"; // Amber/Orange
        if (["DRAFT"].some(k => s.includes(k))) return "#6c757d"; // Gray
        return "#6c757d";
    };

    const getStatusIcon = (status: string | undefined) => {
        const s = (status || "").toUpperCase();
        if (["A", "APPROVED", "POSTED"].some(k => s.includes(k))) return "ti ti-circle-check";
        if (["R", "REJECTED", "CANCELLED"].some(k => s.includes(k))) return "ti ti-circle-x";
        if (["SENT", "PENDING", "TO BE APPROVED"].some(k => s.includes(k))) return "ti ti-clock-hour-4";
        if (["DRAFT"].some(k => s.includes(k))) return "ti ti-file-text";
        return "ti ti-info-circle";
    };



    return (
        <div className="page-wrapper" style={{ backgroundColor: "#f8f9fa" }}>
            <div className="content">

                {/* Page Header matching Reimbursement Header Style */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <div className="d-flex align-items-center">
                            <Link to={routes.employeeDashboard || '#'} className="text-secondary me-3 text-decoration-none bg-white border d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px', borderRadius: '50%' }}>
                                <i className="ti ti-arrow-left" style={{ fontSize: '20px' }}></i>
                            </Link>
                            <div>
                                <h3 className="page-title mb-1 fw-bold text-dark">Leave Overview</h3>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to={routes.employeeDashboard || '#'}>Home</Link>
                                        </li>
                                        <li className="breadcrumb-item">Services</li>
                                        <li className="breadcrumb-item active text-danger">Leave Applications</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-white shadow-sm border px-3 py-2 rounded-3 fw-medium d-flex align-items-center bg-white"
                            onClick={handleSyncSap}
                            disabled={isSyncing}
                        >
                            <i className={`ti ti-refresh me-2 text-primary ${isSyncing ? 'fa-spin' : ''}`} style={{ fontSize: '18px' }}></i>
                            {isSyncing ? 'Syncing...' : 'Sync SAP'}
                        </button>
                        <Link to={`${routes.LeaveManagement.create}`}>
                            <button
                                className="btn text-white fw-bold shadow-sm d-flex align-items-center px-4 rounded-3"
                                style={{ backgroundColor: '#ab1f24', borderColor: '#ab1f24', height: '40px', fontSize: '13px' }}
                            >
                                <i className="ti ti-plus me-1" style={{ fontSize: '16px' }}></i>
                                Create New Request
                            </button>
                        </Link>
                    </div>
                </div>

                {/* List of Requests (Reimbursement Card Style) */}
                <div className="pb-4">
                    {loading === "pending" && !allLeaves ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: '#ab1f24' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : leaveRequests.length > 0 ? (
                        <div className="row g-4">
                            {leaveRequests.map((request: any) => {
                                const AbsenceTypeName = request.AbsenceTypeName || request.LeaveType || 'Leave';
                                const typeColors = getLeaveColorProps(AbsenceTypeName);
                                const status = request.Status || request.StatusName || request.StatusCode || 'Pending';
                                const statusColor = getStatusColor(status);
                                const statusIcon = getStatusIcon(status);
                                const startDate = request.StartDate || request.startDate;
                                const endDate = request.EndDate || request.endDate;
                                const approverName = request.ApproverName || request.SentTo || (request.ApproverUserID ? `ID: ${request.ApproverUserID}` : '-');
                                const stage = request.CurrentStage || request.ApproverLevel;
                                const canDelete =
                                    request.ActionDeleteInd === true ||
                                    request.ActionDeleteInd === 'X' ||
                                    ['SENT', 'PENDING', 'DRAFT', 'NEW', 'T'].some(s => status.toUpperCase().includes(s));

                                return (
                                    <div className="col-12 col-md-6 col-xl-4" key={request.RequestID || request.id}>
                                        <div
                                            className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative hover-card mini-card"
                                            style={{
                                                borderLeft: `5px solid ${statusColor}`,
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                backgroundColor: '#ffffff'
                                            }}
                                        >
                                            <div className="card-body p-3 d-flex flex-column justify-content-between">
                                                <div>
                                                    {/* Header Row: Leave Type on Left, Status Badge on Right */}
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div className="d-flex flex-column overflow-hidden me-2">
                                                            <span className="text-muted xxs text-uppercase fw-bold tracking-wider mb-1">Leave Type</span>
                                                            <div className="fw-bold text-dark small text-truncate d-flex align-items-center" title={AbsenceTypeName}>
                                                                <span
                                                                    className="rounded-circle me-1.5 flex-shrink-0"
                                                                    style={{ width: '8px', height: '8px', backgroundColor: typeColors.color }}
                                                                ></span>
                                                                <span className="text-truncate fw-bold" style={{ fontSize: '13px' }}>{AbsenceTypeName}</span>
                                                            </div>
                                                        </div>
                                                        <span
                                                            className="badge rounded-pill shadow-xs py-1 px-3 border d-flex align-items-center flex-shrink-0"
                                                            style={{
                                                                color: statusColor,
                                                                backgroundColor: '#ffffff',
                                                                borderColor: `${statusColor}44`,
                                                                fontSize: '11px',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            <i className={`${statusIcon} me-1`} style={{ fontSize: '12px' }}></i>
                                                            {status}
                                                        </span>
                                                    </div>

                                                    {/* Boxed Dates Row (Signature Reimbursement Stats Box Style) */}
                                                    <div className="d-flex justify-content-between align-items-center py-2 px-3 mb-3 bg-light-subtle rounded-3">
                                                        <div>
                                                            <div className="xxs text-muted fw-bold text-uppercase">Start Date</div>
                                                            <div className="fw-bold text-dark small d-flex align-items-center mt-0.5">
                                                                <i className="ti ti-calendar me-1 text-primary" style={{ fontSize: '13px' }}></i>
                                                                {formatDate(startDate)}
                                                            </div>
                                                        </div>
                                                        <div className="vr mx-2 opacity-15" style={{ height: '24px' }}></div>
                                                        <div className="text-end">
                                                            <div className="xxs text-muted fw-bold text-uppercase">End Date</div>
                                                            <div className="fw-bold text-dark small d-flex align-items-center justify-content-end mt-0.5">
                                                                <i className="ti ti-calendar-event me-1 text-danger" style={{ fontSize: '13px' }}></i>
                                                                {formatDate(endDate)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Footer Row: Approver info on left, Actions on right */}
                                                <div className="d-flex align-items-center justify-content-between pt-2 border-top mt-auto">
                                                    <div className="d-flex align-items-center overflow-hidden me-2">
                                                        <div
                                                            className="avatar avatar-xs bg-light rounded-circle me-2 d-flex align-items-center justify-content-center border flex-shrink-0"
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            <i className="ti ti-user text-muted" style={{ fontSize: '13px' }}></i>
                                                        </div>
                                                        <div className="d-flex flex-column overflow-hidden">
                                                            <span className="fw-bold text-dark small text-truncate" title={approverName}>
                                                                {approverName}
                                                            </span>
                                                            <span className="text-muted fw-bold text-truncate" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>
                                                                {stage && stage !== '-' ? `STAGE: ${stage}` : 'SENT TO APPROVER'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                        {canDelete && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-xs rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{ width: '32px', height: '32px', padding: 0 }}
                                                                onClick={() => handleDelete(request)}
                                                                title="Delete Request"
                                                            >
                                                                <i className="ti ti-trash" style={{ fontSize: '14px' }}></i>
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-xs rounded-pill px-3 fw-bold d-flex align-items-center"
                                                            style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24", fontSize: '11px', height: '32px' }}
                                                            onClick={() => handleViewDetails(request.RequestID || request.id)}
                                                        >
                                                            View Details <i className="ti ti-chevron-right ms-1" style={{ fontSize: '12px' }}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="card border-0 shadow-sm rounded-4 text-center py-5 bg-white">
                            <div className="card-body">
                                <i className="ti ti-file-off fs-1 text-muted opacity-25 mb-3 d-block" style={{ fontSize: '48px' }}></i>
                                <h4 className="fw-bold text-dark">No Leave Requests Found</h4>
                                <p className="text-muted px-4 mb-3">
                                    You have not submitted any leave requests yet.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Legends Grid */}
                    <div className="mt-5 pt-4 border-top">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="fw-bold text-dark mb-0">Leave Color Legend</h6>
                            <span className="text-muted xxs text-uppercase">Color indicators for leave categories</span>
                        </div>
                        <div className="row g-3">
                            {LEAVE_LEGENDS.map((legend, idx) => (
                                <div className="col-6 col-md-4 col-lg-3 d-flex align-items-center" key={idx}>
                                    <div className="shadow-xs rounded-circle border me-2" style={{ width: '12px', height: '12px', backgroundColor: legend.color, flexShrink: 0 }}></div>
                                    <span style={{ fontSize: '12px', color: '#555', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{legend.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <style>{`
                    .xxs { font-size: 10px; }
                    .hover-card { transition: transform 0.2s, box-shadow 0.2s; }
                    .hover-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(0,0,0,0.08) !important; }
                    .btn-white { background-color: #fff; color: #444; }
                    .btn-white:hover { background-color: #f8f9fa; }
                    .bg-light-subtle { background-color: #f8f9fa !important; }
                    .shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
                    .fa-spin { animation: fa-spin 2s infinite linear; }
                    @keyframes fa-spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(359deg); }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default LeaveManagementList;
